import { useState, useEffect } from 'react';
import SolicitorHeader from '../../components/headers/SolicitorHeader';
import {
  SearchPanel,
  ResultsTable,
  Pagination,
  EmptyState,
  NoResults,
} from '../../components/features/documentCatalog';
import useDocumentCatalogSearch from '../../hooks/features/documentCatalog/useDocumentCatalogSearch';
import '../../styles/AccountCreate.css';
import '../../styles/button.css';
import '../../styles/InsuranceApplicationList.css';
import '../../styles/DocumentCatalogSearch.css';
import iconTitle from '../../assets/img/icon-square.svg';
import SidebarIcon from '../../components/features/documentCatalog/SidebarIcon';

/**
 * 搭載文書検索ページ
 *
 * 既存のAI検索（テキスト・ベクトル検索）とは独立した、
 * 文書メタデータによるフィルタリング専用の検索画面。
 * ルート: /solicitor/document-catalog（solicitor / agency ロールがアクセス可能）
 *
 * useDocumentCatalogSearch() を呼び出して全状態・ハンドラを取得し、
 * SearchPanel（検索条件サイドバー）と ResultsTable（結果一覧）に渡す。
 *
 * 表示切替ロジック:
 * - isLoading=true → ローディング表示
 * - hasSearched=false → EmptyState（検索前の案内）
 * - hasSearched=true かつ items.length===0 → NoResults（該当なし）
 * - items.length>0 → ResultsTable + Pagination
 */

const MOBILE_BREAKPOINT = 768;

const DocumentCatalogSearch = () => {
  const [sidebarMinimized, setSidebarMinimized] = useState(false);
  const toggleSidebarMinimized = () => setSidebarMinimized((prev) => !prev);
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarActive, setSidebarActive] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const {
    // 検索条件
    insuranceType,
    selectedDocumentTypeId,
    selectedCompanyIds,
    selectedCategories,
    dateFrom,
    dateTo,
    keyword,
    // マスターデータ
    insuranceCompanies,
    documentTypesAccess,
    // 検索条件ハンドラ
    handleInsuranceTypeChange,
    handleDocumentTypeChange,
    setSelectedCompanyIds,
    setSelectedCategories,
    setDateFrom,
    setDateTo,
    setKeyword,
    // 検索結果
    items,
    page,
    totalPages,
    isLoading,
    error,
    hasSearched,
    // オーケストレーターハンドラ
    handleSearch,
    handlePageChange,
    handleReset,
    handleNavigateToDetail,
  } = useDocumentCatalogSearch();

  const handleSearchWithClose = () => {
    handleSearch();
    if (isMobile) setSidebarActive(false);
  };

  return (
    <div className="insurance-search-page">
      <SolicitorHeader />
      {isMobile && (
        <div className="dc-sidebar-toggle-container">
          <button
            className="dc-sidebar-toggle-button"
            onClick={() => setSidebarActive((prev) => !prev)}
          >
            <span className="c-display-flex c-display-flex--gap10">
              <SidebarIcon stroke="#ffffff" />
              {sidebarActive ? '閉じる' : '文書検索'}
            </span>
          </button>
        </div>
      )}
      <div className="page-container">
        <div className={`insurance-layout${isMobile ? ' mobile' : ''}`}>
          <SearchPanel
            insuranceType={insuranceType}
            selectedDocumentTypeId={selectedDocumentTypeId}
            selectedCompanyIds={selectedCompanyIds}
            selectedCategories={selectedCategories}
            dateFrom={dateFrom}
            dateTo={dateTo}
            keyword={keyword}
            insuranceCompanies={insuranceCompanies}
            documentTypesAccess={documentTypesAccess}
            onInsuranceTypeChange={handleInsuranceTypeChange}
            onDocumentTypeChange={handleDocumentTypeChange}
            onCompanyChange={setSelectedCompanyIds}
            onCategoryChange={setSelectedCategories}
            onDateFromChange={setDateFrom}
            onDateToChange={setDateTo}
            onKeywordChange={setKeyword}
            onSearch={handleSearchWithClose}
            onReset={handleReset}
            isLoading={isLoading}
            sidebarMinimized={sidebarMinimized}
            onToggleSidebar={toggleSidebarMinimized}
            isMobile={isMobile}
            sidebarActive={sidebarActive}
          />
          <main className={`insurance-results${isMobile || sidebarMinimized ? ' fullwidth' : ''}`}>
            <div className="page-header">
              <div className="title-section">
                <div className="icon-square">
                  <img src={iconTitle} alt="タイトルアイコン" />
                </div>
                <h2>搭載文書検索結果一覧</h2>
              </div>
            </div>
            {isLoading && (
              <div className="no-results">検索中...</div>
            )}
            {!isLoading && error && (
              <div className="dc-error-state">
                検索中にエラーが発生しました。しばらく時間をおいて再度お試しください。
              </div>
            )}
            {!isLoading && !error && !hasSearched && <EmptyState />}
            {!isLoading && !error && hasSearched && items.length === 0 && <NoResults />}
            {!isLoading && !error && items.length > 0 && (
              <>
                <ResultsTable items={items} onNavigate={handleNavigateToDetail} />
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default DocumentCatalogSearch;
